import { DEAL_STAGES } from '@/helpers/hubspot/constants';
import { CreateDealData, Deal } from '@/helpers/hubspot/types';
import logError from '@/helpers/logError';
import { Dispatch } from 'react';
import { RepairFormState } from '../constants';
import { RepairFormActionMap, RepairFormActions } from '../reducer';
import profiler from '@/helpers/userProfile/profiler';
import updateHubspotDeal from './updateHubspotDeal';

export const getHubspotDeal = async (
	state: RepairFormState,
	dispatch: Dispatch<RepairFormActionMap>,
) => {
	if (state.hubspotDealId) {
		// Update the deal to ensure the stage is correct.
		const success = await updateHubspotDeal(state);
		if (!success) logError(new Error('Failed to update HubSpot deal'));
		// We don't want to fail the form submission if the deal update fails.

		return state.hubspotDealId;
	}

	const [userProfile] = profiler();
	const isOutOfServiceArea = state.availableDealerCount === 0;

	const createDealBody: CreateDealData = {
		properties: {
			city: userProfile.city,
			dealname: `${state.firstName} ${state.lastName} - ${state.referenceId}`,
			dealstage: isOutOfServiceArea
				? DEAL_STAGES.OUT_OF_SERVICE // If there are no dealers in the area, set the deal stage to 'Out of Service'
				: DEAL_STAGES.INTAKE,
			experience: state.experience,
			full_address: state.address,
			message: state.message,
			model_number: state.modelNumber,
			model_type: state.modelType,
			system_location: state.systemLocation,
			system_age: state.systemAge,
			model_size: state.modelSize,
			referral_source: state.referralSource,
			reference_id: state.referenceId,
			session_id: state.sessionId,
			state: userProfile.state,
			system_issues: state.systemIssues.join(', '),
			buying_criteria: state.buyingCriteria.join(';'),
			replacement_timeline: state.urgency,
			zip_code: userProfile.zip,
		},
	};

	const deal = (await fetch('/api/hubspot/deal', {
		method: 'POST',
		body: JSON.stringify(createDealBody),
	})
		.then((response) => response.json())
		.catch((error) => {
			if (error instanceof Error) {
				logError(error);
			}
			return null;
		})) as Deal | null;

	if (!deal || !deal.id) {
		logError(new Error('Failed to initialize HubSpot deal'));
		return null;
	}

	const hubspotDealId = deal.id;
	dispatch({
		type: RepairFormActions.SET_HUBSPOT_DEAL_ID,
		payload: { hubspotDealId },
	});

	return hubspotDealId;
};
